import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  ButtonGroup,
  Typography,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  FileCopy as DuplicateIcon,
  Delete as DeleteIcon,
  Timeline as TimelineIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import api from '../utils/api';
import { PIPELINE_STATUS } from '../utils/constants';
import PipelineCard from '../components/pipeline/PipelineCard';
import useAuthStore from '../store/authStore';

const PipelinesPage = () => {
  const navigate = useNavigate();
  const isAdmin = Boolean(useAuthStore((state) => state.user?.is_superuser));
  
  // States
  const [pipelines, setPipelines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [duplicateName, setDuplicateName] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filter, setFilter] = useState('all');
  const [scope, setScope] = useState('all');
  
  // Fetch pipelines
  useEffect(() => {
    const fetchPipelines = async () => {
      setIsLoading(true);
      try {
        const response = await api.get(`/pipelines?scope=${scope}`);
        setPipelines(response.data);
      } catch (error) {
        console.error('Error fetching pipelines:', error);
        setSnackbar({
          open: true,
          message: 'Error loading pipelines',
          severity: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPipelines();
  }, [scope]);
  
  // Filter pipelines
  const filteredPipelines = filter === 'all'
    ? pipelines
    : pipelines.filter((pipeline) => pipeline.status === filter);
  
  // Handle menu open
  const handleMenuOpen = (event, pipeline) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedPipeline(pipeline);
  };
  
  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  // Handle pipeline deletion
  const handleDeletePipeline = async () => {
    if (!selectedPipeline) return;
    
    try {
      await api.delete(`/pipelines/${selectedPipeline.id}`);
      
      setPipelines(pipelines.filter((p) => p.id !== selectedPipeline.id));
      setSnackbar({
        open: true,
        message: 'Pipeline deleted successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting pipeline:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting pipeline',
        severity: 'error',
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedPipeline(null);
      handleMenuClose();
    }
  };
  
  // Handle pipeline duplication
  const handleDuplicatePipeline = async () => {
    if (!selectedPipeline || !duplicateName) return;
    
    try {
      const response = await api.post(
        `/pipelines/${selectedPipeline.id}/duplicate`,
        { source_id: selectedPipeline.id, new_name: duplicateName }
      );
      
      setPipelines([...pipelines, response.data]);
      setSnackbar({
        open: true,
        message: 'Pipeline duplicated successfully',
        severity: 'success',
      });
      navigate(`/pipelines/${response.data.id}`);
    } catch (error) {
      console.error('Error duplicating pipeline:', error);
      setSnackbar({
        open: true,
        message: 'Error duplicating pipeline',
        severity: 'error',
      });
    } finally {
      setIsDuplicateDialogOpen(false);
      setDuplicateName('');
      setSelectedPipeline(null);
      handleMenuClose();
    }
  };
  
  // Handle pipeline run
  const handleRunPipeline = async (pipeline) => {
    try {
      await api.post('/runs/', { pipeline_id: pipeline.id });
      
      setSnackbar({
        open: true,
        message: 'Pipeline started successfully',
        severity: 'success',
      });
      
      // Navigate to runs page
      navigate(`/runs?pipeline_id=${pipeline.id}`);
    } catch (error) {
      console.error('Error running pipeline:', error);
      setSnackbar({
        open: true,
        message: 'Error starting pipeline',
        severity: 'error',
      });
    }
  };
  
  // Handle filter change
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  
  // Open the duplicate dialog for a pipeline
  const openDuplicate = (pipeline) => {
    setSelectedPipeline(pipeline);
    setDuplicateName(`Copy of ${pipeline.name}`);
    setIsDuplicateDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Pipelines</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/pipelines/new')}
        >
          Create Pipeline
        </Button>
      </Box>

      <Paper sx={{ width: '100%', mb: 3, p: 3 }}>
          <Box sx={{ mb: 2 }}>
            <ButtonGroup size="small">
              {[['mine', 'Mine'], ['public', 'Public'], ['all', isAdmin ? 'All (admin)' : 'All']].map(([value, label]) => (
                <Button
                  key={value}
                  variant={scope === value ? 'contained' : 'outlined'}
                  onClick={() => setScope(value)}
                >
                  {label}
                </Button>
              ))}
            </ButtonGroup>
          </Box>
          <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant={filter === 'all' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleFilterChange('all')}
            >
              All
            </Button>
            <Button
              variant={filter === PIPELINE_STATUS.ACTIVE ? 'contained' : 'outlined'}
              size="small"
              color="success"
              onClick={() => handleFilterChange(PIPELINE_STATUS.ACTIVE)}
            >
              Active
            </Button>
            <Button
              variant={filter === PIPELINE_STATUS.DRAFT ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleFilterChange(PIPELINE_STATUS.DRAFT)}
            >
              Draft
            </Button>
            <Button
              variant={filter === PIPELINE_STATUS.PAUSED ? 'contained' : 'outlined'}
              size="small"
              color="warning"
              onClick={() => handleFilterChange(PIPELINE_STATUS.PAUSED)}
            >
              Paused
            </Button>
            <Button
              variant={filter === PIPELINE_STATUS.ARCHIVED ? 'contained' : 'outlined'}
              size="small"
              color="error"
              onClick={() => handleFilterChange(PIPELINE_STATUS.ARCHIVED)}
            >
              Archived
            </Button>
          </Box>
          
          {filteredPipelines.length > 0 ? (
            <Box sx={{ containerType: 'inline-size' }}>
              <Box
                sx={{
                  display: 'grid',
                  gap: 3,
                  gridTemplateColumns: '1fr',
                  // Columns follow the space the list actually has (the sidebar
                  // eats into the viewport), not the viewport width.
                  '@container (min-width: 600px)': {
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    // Side by side, reserve two title lines so chip rows line up.
                    '& .pipeline-card-title': { minHeight: '2.7em' },
                  },
                  '@container (min-width: 920px)': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
                  '@container (min-width: 1360px)': { gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' },
                }}
              >
                {filteredPipelines.map((pipeline) => (
                  <PipelineCard
                    key={pipeline.id}
                    pipeline={pipeline}
                    onOpen={(p) => navigate(`/pipelines/${p.id}`)}
                    onDuplicate={openDuplicate}
                    onRun={handleRunPipeline}
                    onMenuOpen={handleMenuOpen}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center', 
                py: 8,
                bgcolor: 'background.paper',
                borderRadius: 1,
                boxShadow: 1,
              }}
            >
              <TimelineIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No pipelines found
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph align="center">
                {filter === 'all'
                  ? 'Get started by creating your first pipeline'
                  : `No pipelines with status "${filter}" found`}
              </Typography>
              {filter === 'all' && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/pipelines/new')}
                >
                  Create Pipeline
                </Button>
              )}
            </Box>
          )}
      </Paper>
      
      {/* Pipeline Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {selectedPipeline?.can_edit && (
          <MenuItem onClick={() => {
            handleMenuClose();
            navigate(`/pipelines/${selectedPipeline.id}`);
          }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        )}
        
        <MenuItem onClick={() => {
          handleMenuClose();
          openDuplicate(selectedPipeline);
        }}>
          <DuplicateIcon fontSize="small" sx={{ mr: 1 }} />
          Duplicate
        </MenuItem>
        
        <MenuItem onClick={() => {
          handleMenuClose();
          navigate(`/runs?pipeline_id=${selectedPipeline.id}`);
        }}>
          <DescriptionIcon fontSize="small" sx={{ mr: 1 }} />
          View Runs
        </MenuItem>
        
        {selectedPipeline?.can_edit && <Divider />}
        
        {selectedPipeline?.can_edit && (
          <MenuItem 
            onClick={() => {
              handleMenuClose();
              setIsDeleteDialogOpen(true);
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Pipeline</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the pipeline "{selectedPipeline?.name}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeletePipeline} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Duplicate Dialog */}
      <Dialog
        open={isDuplicateDialogOpen}
        onClose={() => setIsDuplicateDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Duplicate Pipeline</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a copy of "{selectedPipeline?.name}" with a new name:
          </DialogContentText>
          <TextField
            autoFocus
            label="New Pipeline Name"
            value={duplicateName}
            onChange={(e) => setDuplicateName(e.target.value)}
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDuplicateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDuplicatePipeline} 
            color="primary"
            disabled={!duplicateName}
          >
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PipelinesPage;
